import { useEffect, useRef, useState } from "react";
import mqtt, { MqttClient, IClientOptions } from "mqtt";
export const WS_URL = import.meta.env.VITE_SOCKET_URL || "ws://localhost:8080";

// Interfaces to match the Dart implementation
interface MessageData {
  payload: string | Buffer<ArrayBufferLike>;
  topic: string;
}

interface MqttConnectionStatus {
  isConnected: boolean;
  message: string;
}

interface MqttTopicStatus {
  isSubscribed: boolean;
  topic: string;
}

// Main hook implementation
const useMqttClient = (maxMessages = 100) => {
  const clientRef = useRef<MqttClient | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<boolean>(false);
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [subscribedTopics, setSubscribedTopics] = useState<MqttTopicStatus[]>(
    [],
  );
  // console.log("messages", messages);
  // Generate a unique client ID similar to the Dart implementation
  const generateClientId = () => {
    return `web_client_${Math.random().toString(16).substring(2, 10)}`;
  };

  useEffect(() => {
    const options: IClientOptions = {
      username: import.meta.env.VITE_MQTT_USER || "",
      password: import.meta.env.VITE_MQTT_PASS || "",
      clientId: generateClientId(),
      keepalive: 60,
      connectTimeout: 10000,
      reconnectPeriod: 5000,
      clean: true,
      path: "/mqtt",
      rejectUnauthorized: false, // SSL Secure = false
    };

    // Sử dụng kết nối WebSocket theo cấu hình form
    const client = mqtt.connect(`${WS_URL}/mqtt`, options);
    console.log("🔌 Kết nối WebSocket:", `${WS_URL}/mqtt`);

    // Connected callback
    client.on("connect", () => {
      console.log("✅ MQTT Connected!");
      setConnectionStatus(true);
    });

    // Disconnected callback
    client.on("disconnect", () => {
      console.log("❌ MQTT Disconnected!");
      setConnectionStatus(false);
    });

    // Thêm xử lý sự kiện offline
    client.on("offline", () => {
      console.log("❌ MQTT Offline!");
      setConnectionStatus(false);
    });

    // Message callback
    client.on("message", (topic, message) => {
      console.log("message", JSON.parse(message.toString()));
      const payload = JSON.parse(message.toString());
      // console.log(`📤 Received from topic: ${topic}`);
      setMessages((prev) => {
        const newMessages = [...prev, { payload, topic }];
        // Chỉ giữ lại maxMessages tin nhắn gần nhất
        return newMessages.slice(-maxMessages);
      });
    });

    // Error callback
    client.on('error', (err) => {
      console.error('❌ Connection error:', err.message);
      client.end();
    });
    clientRef.current = client;

    return () => {
      if (clientRef.current) {
        clientRef.current.end();
      }
    };
  }, []);

  // Subscribe to a topic
  const subscribe = (topic: string) => {
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.subscribe(topic, { qos: 1 }, (error) => {
        if (!error) {
          // console.log(`📌 Subscribed to: ${topic}`);
          setSubscribedTopics((prev) => [
            ...prev.filter((t) => t.topic !== topic),
            { isSubscribed: true, topic },
          ]);
        } else {
          console.log(`Failed to subscribe ${topic}`);
          setSubscribedTopics((prev) => [
            ...prev.filter((t) => t.topic !== topic),
            { isSubscribed: false, topic },
          ]);
        }
      });
    }
  };

  // Unsubscribe from a topic
  const unsubscribe = (topic: string) => {
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.unsubscribe(topic, {}, () => {
        console.log(`Unsubscribed topic: ${topic}`);
        setSubscribedTopics((prev) =>
          prev
            .filter((t) => t.topic !== topic)
            .concat({ isSubscribed: false, topic }),
        );
      });
    }
  };

  // Publish a message to a topic
  const publish = (topic: string, message: string) => {
    if (clientRef.current && clientRef.current.connected) {
      try {
        clientRef.current.publish(topic, message, { qos: 1 }, (error) => {
          if (error) {
            console.error(`Lỗi gửi tin đến ${topic}:`, error);
            return false;
          }
          console.log(`📤 Đã gửi đến ${topic}: ${message}`);
        });
        return true; // Trả về true nếu gửi thành công
      } catch (err) {
        console.error("Lỗi khi publish:", err);
        return false;
      }
    }
    return false;
  };

  // Connect manually (in case auto-connect fails or is disabled)
  const connect = async (): Promise<MqttConnectionStatus> => {
    return new Promise((resolve) => {
      if (!clientRef.current) {
        resolve({
          isConnected: false,
          message: "Client not initialized",
        });
        return;
      }

      if (clientRef.current.connected) {
        resolve({
          isConnected: true,
          message: "Already connected",
        });
        return;
      }

      // Set up one-time event handlers for this connection attempt
      const onConnect = () => {
        clientRef.current?.removeListener("connect", onConnect);
        clientRef.current?.removeListener("error", onError);
        resolve({
          isConnected: true,
          message: "Connected Successfully",
        });
      };

      const onError = (err: Error) => {
        clientRef.current?.removeListener("connect", onConnect);
        clientRef.current?.removeListener("error", onError);
        resolve({
          isConnected: false,
          message: `Failed to Connect: ${err.message}`,
        });
      };

      clientRef.current.once("connect", onConnect);
      clientRef.current.once("error", onError);

      // Attempt reconnection
      clientRef.current.reconnect();
    });
  };

  // Disconnect from the broker
  const disconnect = () => {
    if (clientRef.current) {
      clientRef.current.end();
    }
  };

  // Thêm hàm để huỷ đăng ký tất cả các topic
  const unsubscribeAll = () => {
    if (clientRef.current && clientRef.current.connected) {
      subscribedTopics.forEach((topicStatus) => {
        if (topicStatus.isSubscribed) {
          clientRef.current!.unsubscribe(topicStatus.topic, {}, () => {
            console.log(`Unsubscribed from topic: ${topicStatus.topic}`);
          });
        }
      });
      setSubscribedTopics([]); // Reset danh sách topic đã đăng ký
      setMessages([]); // Xóa các tin nhắn cũ
    }
  };

  // Return the client reference and utility functions
  return {
    client: clientRef,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    unsubscribeAll,
    publish,
    connectionStatus,
    messages,
    subscribedTopics,
  };
};

export default useMqttClient;
